import { useQuery } from '@tanstack/react-query';

import { ICON_URL_STALE_TIME } from '../../app/constants';
import { queryClient } from '../../api/queryClient';
import {
  getV1IdentityCurrentInvitesToJoinOptions,
  getV1IdentityCurrentInvitesToJoinQueryKey,
  getV1IdentityCurrentRequestToJoinByIdOptions,
  getV1IdentityCurrentRequestsToJoinOptions,
  getV1IdentityCurrentRequestsToJoinQueryKey,
} from '../../api/generated/pap/@tanstack/react-query.gen';
import {
  getV1IdentityCurrentRequestBusinessPartnersOptions,
  getV1IdentityCurrentRequestBusinessPartnersQueryKey,
} from '../../api/generated/pm/@tanstack/react-query.gen';

/**
 * Query hooks for the Companies page, all read from the current Identity's
 * side: the Requests to Join it has sent (and one such request's reviewers),
 * the Invites to Join it has received, and the Business Partner Requests it
 * has made. See `../Settings/IdentityProviders/identityProviders.queries.ts`
 * for the pattern.
 */

/** Requests to Join the current Identity has sent. */
export const useIdentityCurrentRequests = () =>
  useQuery(getV1IdentityCurrentRequestsToJoinOptions());

export const invalidateIdentityCurrentRequests = () =>
  queryClient.invalidateQueries({ queryKey: getV1IdentityCurrentRequestsToJoinQueryKey() });

/** One of the current Identity's Requests to Join, with the reviewers who will approve it. */
export const useRequestDetails = (id?: string) =>
  useQuery({
    ...getV1IdentityCurrentRequestToJoinByIdOptions({ path: { id: id as string } }),
    enabled: !!id,
  });

/**
 * Pending Invites to Join for the current Identity. Resolves to the `items`
 * array. Icon URLs in the response are presigned and expire, hence the stale
 * time.
 */
export const useInvitesToJoin = () =>
  useQuery({
    ...getV1IdentityCurrentInvitesToJoinOptions(),
    select: (data) => data.items,
    staleTime: ICON_URL_STALE_TIME,
    refetchOnMount: true,
  });

export const invalidateInvitesToJoin = () =>
  queryClient.invalidateQueries({ queryKey: getV1IdentityCurrentInvitesToJoinQueryKey() });

/** Business Partner Requests the current Identity has made. */
export const useCurrentIdentityRequestBusinessPartners = () =>
  useQuery(getV1IdentityCurrentRequestBusinessPartnersOptions());

export const invalidateCurrentIdentityRequestBusinessPartners = () =>
  queryClient.invalidateQueries({
    queryKey: getV1IdentityCurrentRequestBusinessPartnersQueryKey(),
  });
